import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import "./css/styles.css";
import "./css/react-tags.css";

import { AuthProvider } from "./contexts/AuthContext";
import { LocationsProvider } from "./contexts/LocationsContext";
import { StoriesProvider } from "./contexts/StoriesContext";
import { TagsProvider } from "./contexts/TagsContext";
import { RoomsProvider } from "./contexts/RoomsContext";

import ProtectedRoute from "./pages/ProtectedRoute";
import AppLayout from "./pages/AppLayout";
import Login from "./pages/Login";
import AccountCreate from "./pages/AccountCreate";
import PageNotFound from "./pages/PageNotFound";

// Lazy-load page components
const Homepage = lazy(() => import("./pages/Homepage"));
const Locations = lazy(() => import("./pages/Locations"));
const LocationEdit = lazy(() => import("./pages/LocationEdit"));
const LocationNew = lazy(() => import("./pages/LocationNew"));
const MemoryRooms = lazy(() => import("./pages/MemoryRooms"));
const Timeline = lazy(() => import("./pages/Timeline"));
const Connections = lazy(() => import("./pages/Connections"));
const Story = lazy(() => import("./pages/Story"));
const StoryNew = lazy(() => import("./pages/StoryNew"));
const StoryEdit = lazy(() => import("./pages/StoryEdit"));
const StoryMap = lazy(() => import("./pages/StoryMap"));
const Media = lazy(() => import("./pages/Media"));
const MediaEdit = lazy(() => import("./pages/MediaEdit"));
const MediaUpload = lazy(() => import("./pages/MediaUpload"));
const Camera = lazy(() => import("./pages/Camera"));
const TagManager = lazy(() => import("./pages/TagManager"));
const Chat = lazy(() => import("./pages/Chat"));
const RoomDetail = lazy(() => import("./pages/RoomDetail"));
const MemoryRoomEventDetail = lazy(() => import("./pages/MemoryRoomEventDetail"));
const MemoryRoomLocationDetail = lazy(() => import("./pages/MemoryRoomLocationDetail"));
const AccountDetail = lazy(() => import("./pages/AccountDetail"));
const AccountSnapShot = lazy(() => import("./pages/AccountSnapshot"));
const AccountEdit = lazy(() => import("./pages/AccountEdit"));
const ProfilePic = lazy(() => import("./pages/ProfilePic"));
const ProfileMasthead = lazy(() => import("./pages/ProfileMasthead"));

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LocationsProvider>
          <StoriesProvider>
            <RoomsProvider>
              <TagsProvider>
                <BrowserRouter
                  future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                  }}
                >
                  <Suspense fallback={<div>Loading.......</div>}>
                    <Routes>
                      <Route index element={<Login />} />
                      <Route path="accountcreate" element={<AccountCreate />} />
                      <Route
                        path="/app"
                        element={
                          <ProtectedRoute>
                            <AppLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route path="home" element={<Homepage />} />
                        <Route path="locations" element={<Locations />} />
                        <Route
                          path="locations/:id"
                          element={<LocationEdit />}
                        />
                        <Route path="locationnew" element={<LocationNew />} />
                        <Route path="memories" element={<MemoryRooms />} />
                        <Route path="memories/:id" element={<RoomDetail />} />
                        <Route
                          path="memories/:id/events/:eventId"
                          element={<MemoryRoomEventDetail />}
                        />
                        <Route
                          path="memories/:id/locations/:locId"
                          element={<MemoryRoomLocationDetail />}
                        />
                        <Route path="timeline" element={<Timeline />} />
                        <Route path="connections" element={<Connections />} />
                        <Route path="story" element={<Story />} />
                        <Route path="story/new" element={<StoryNew />} />
                        <Route path="story/:id" element={<StoryEdit />} />
                        <Route path="storymap/:id" element={<StoryMap />} />
                        <Route path="media" element={<Media />} />
                        <Route path="camera" element={<Camera />} />
                        <Route path="media/:id" element={<MediaEdit />} />
                        <Route path="media/upload" element={<MediaUpload />} />
                        <Route path="tags" element={<TagManager />} />
                        <Route path="chat" element={<Chat />} />
                        <Route path="account" element={<AccountDetail />} />
                        <Route
                          path="account/:screenname"
                          element={<AccountSnapShot />}
                        />
                        <Route path="accountedit" element={<AccountEdit />} />
                        <Route
                          path="accountedit/profilepic"
                          element={<ProfilePic />}
                        />
                        <Route
                          path="accountedit/profilemasthead"
                          element={<ProfileMasthead />}
                        />
                      </Route>
                      <Route path="*" element={<PageNotFound />} />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </TagsProvider>
            </RoomsProvider>
          </StoriesProvider>
        </LocationsProvider>
      </AuthProvider>
    </div>
  );
}
